import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { parseProfileAward, type ProfileAward } from '../services/users/rowerService';
import { useCoachEffectiveTeamId } from './useCoachEffectiveTeamId';
import { isAwaitingParentalConsent } from '../utils/rosterConsentGate';

export type CoachAthleteAccessState = 'noActiveTeam' | 'notLinked' | 'awaitingConsent' | 'allowed';

export type ProfileVisibility = Record<string, boolean>;

/** Visible after team consent unless athlete sets profile_visibility.email to false. */
export function isCoachEmailVisible(visibility: ProfileVisibility): boolean {
  return visibility.email !== false;
}

export interface CoachAthleteSummary {
  email: string | null;
  avatar_url: string | null;
  date_of_birth: string | null;
  gender: 'male' | 'female' | 'open' | null;
  height_cm: number | null;
  weight_kg: number | null;
  club: string | null;
  erg_2k_seconds: number | null;
  gpa: number | null;
  sat_score: number | null;
  act_score: number | null;
  bio: string | null;
  phone: string | null;
  based_city: string | null;
  based_region: string | null;
  based_country_code: string | null;
  social_links: Record<string, unknown> | null;
  profile_links: Array<Record<string, unknown>> | null;
  profile_awards: ProfileAward[];
  profile_visibility: ProfileVisibility;
  /** Unnormalized rower_profiles.profile_visibility (includes concept2_training team map). */
  profile_visibility_raw: unknown;
}

function parseErgSeconds(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

function normalizeVisibility(raw: unknown): ProfileVisibility {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const out: ProfileVisibility = {};
  for (const [k, v] of Object.entries(o)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  // backward compat: sex -> gender
  if (out.sex !== undefined && out.gender === undefined) out.gender = out.sex;
  delete out.sex;
  return out;
}

/** Email for coach roster/journey UI; null when athlete hid email or value is empty. */
export function getCoachVisibleEmail(rawEmail: unknown, profileVisibility: unknown): string | null {
  if (!isCoachEmailVisible(normalizeVisibility(profileVisibility))) return null;
  const email = typeof rawEmail === 'string' ? rawEmail.trim() : '';
  return email || null;
}

/** Height/weight/erg etc. — visible only when athlete sets profile_visibility[key] to true. */
export function isCoachProfileFieldVisible(profileVisibility: unknown, fieldKey: string): boolean {
  return normalizeVisibility(profileVisibility)[fieldKey] === true;
}

function parseProfileNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

/** Numeric profile field for coach roster UI; null when hidden or empty. */
export function getCoachVisibleProfileNumber(
  raw: unknown,
  profileVisibility: unknown,
  visibilityKey: string
): number | null {
  if (!isCoachProfileFieldVisible(profileVisibility, visibilityKey)) return null;
  return parseProfileNumber(raw);
}

/** Roster row for consent + coach profile access. */
type RosterEntryRow = {
  id: string;
  consent_given_at: string | null;
  consent_withdrawn_at: string | null;
};

/**
 * Demo athletes only appear on the demo team's roster. Coaches often have another team as
 * "current" (useCoachEffectiveTeamId / useCurrentTeam). If (coachTeam, rower) has no roster row, fall back to the
 * demo team roster entry for this rower so journey/profile details still resolve.
 */
async function resolveCoachRosterEntry(
  teamId: string,
  rowerProfileId: string
): Promise<RosterEntryRow | null> {
  const { data: primary, error: e1 } = await supabase
    .from('team_roster_entries')
    .select('id, consent_given_at, consent_withdrawn_at')
    .eq('team_id', teamId)
    .eq('rower_id', rowerProfileId)
    .maybeSingle();

  if (e1) throw e1;
  if (primary) return primary as RosterEntryRow;

  const { data: demoTeam, error: e2 } = await supabase
    .from('teams')
    .select('id')
    .eq('is_demo', true)
    .limit(1)
    .maybeSingle();

  if (e2) throw e2;
  if (!demoTeam?.id || typeof demoTeam.id !== 'string') return null;

  const { data: demoEntry, error: e3 } = await supabase
    .from('team_roster_entries')
    .select('id, consent_given_at, consent_withdrawn_at')
    .eq('team_id', demoTeam.id)
    .eq('rower_id', rowerProfileId)
    .maybeSingle();

  if (e3) throw e3;
  return demoEntry as RosterEntryRow | null;
}

/** Coach team id for athlete summary consent checks (real team, or demo when enabled). */
export function useCoachAthleteSummaryTeamId(rosterTeamIdOverride?: string | null): string | null {
  const { teamId } = useCoachEffectiveTeamId();
  return rosterTeamIdOverride ?? teamId;
}

export function useCoachAthleteSummary(
  teamId: string | null | undefined,
  rowerProfileId: string | null
) {
  return useQuery({
    queryKey: ['coach-athlete-summary', teamId, rowerProfileId],
    enabled: !!rowerProfileId,
    staleTime: 60_000,
    queryFn: async () => {
      if (!rowerProfileId) throw new Error('Missing rowerProfileId');
      if (!teamId) {
        return {
          accessState: 'noActiveTeam' as const,
          summary: null as CoachAthleteSummary | null,
          awaitingParentalConsent: false,
        };
      }

      const entry = await resolveCoachRosterEntry(teamId, rowerProfileId);

      if (!entry) {
        return {
          accessState: 'notLinked' as const,
          summary: null as CoachAthleteSummary | null,
          awaitingParentalConsent: false,
        };
      }
      if (!entry.consent_given_at) {
        // Age-gate this specific consent-missing case: only minors (or a prior parental
        // withdrawal) hard-block the athlete's journey/profile chrome for the coach.
        const { data: dobRow } = await supabase
          .from('rower_profiles')
          .select('date_of_birth')
          .eq('id', rowerProfileId)
          .maybeSingle();

        const awaitingParentalConsent = isAwaitingParentalConsent({
          consentGivenAt: entry.consent_given_at,
          consentWithdrawnAt: entry.consent_withdrawn_at,
          dateOfBirth: (dobRow?.date_of_birth as string | null) ?? null,
        });

        return {
          accessState: 'awaitingConsent' as const,
          summary: null as CoachAthleteSummary | null,
          awaitingParentalConsent,
        };
      }

      // Cast to any: generated types predate the primary-info migration columns
      const { data: profileRaw, error: profileError } = await supabase
        .from('rower_profiles')
        .select(
          'email, avatar_url, date_of_birth, gender, height_cm, weight_kg, club, erg_2k_seconds, gpa, sat_score, act_score, bio, phone, based_city, based_region, based_country_code, social_links, profile_links, profile_awards, profile_visibility'
        )
        .eq('id', rowerProfileId)
        .single();

      if (profileError) throw profileError;

      const p = profileRaw as unknown as Record<string, unknown>;
      const rawAwards = p.profile_awards;
      const profile_awards: ProfileAward[] = Array.isArray(rawAwards)
        ? rawAwards.map(parseProfileAward).filter((x): x is ProfileAward => x !== null)
        : [];

      const rawEmail = p.email;
      const email = typeof rawEmail === 'string' && rawEmail.trim() ? rawEmail.trim() : null;

      const summary: CoachAthleteSummary = {
        email,
        avatar_url: (p.avatar_url as string | null) ?? null,
        date_of_birth: (p.date_of_birth as string | null) ?? null,
        gender: (p.gender as CoachAthleteSummary['gender']) ?? null,
        height_cm: (p.height_cm as number | null) ?? null,
        weight_kg: (p.weight_kg as number | null) ?? null,
        club: (p.club as string | null) ?? null,
        erg_2k_seconds: parseErgSeconds(p.erg_2k_seconds),
        gpa: parseProfileNumber(p.gpa),
        sat_score: parseProfileNumber(p.sat_score),
        act_score: parseProfileNumber(p.act_score),
        bio: (p.bio as string | null) ?? null,
        phone: (p.phone as string | null) ?? null,
        based_city: (p.based_city as string | null) ?? null,
        based_region: (p.based_region as string | null) ?? null,
        based_country_code: (p.based_country_code as string | null) ?? null,
        social_links: (p.social_links as Record<string, unknown> | null) ?? null,
        profile_links: (p.profile_links as Array<Record<string, unknown>> | null) ?? null,
        profile_awards,
        profile_visibility: normalizeVisibility(p.profile_visibility),
        profile_visibility_raw: p.profile_visibility ?? null,
      };

      return { accessState: 'allowed' as const, summary, awaitingParentalConsent: false };
    },
  });
}
